import React, { type JSX } from 'react';
import { type Root, createRoot } from 'react-dom/client';
import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import TranslationsProvider from '@naturehouse/ui-library/providers/TranslationsProvider';
import type { PriceDetailsProps } from '@naturehouse/ui-library/components/01-molecules/PriceDetails/PriceDetails';
import PriceDetails from '@naturehouse/ui-library/components/01-molecules/PriceDetails/PriceDetails';
import type { Translations } from '@naturehouse/ui-library/providers/TranslationsProvider.types';
import { getLocale } from '@naturehouse/nh-essentials/lib/dates/date';
import renderPriceDetailsEvents from '../../../assets/js/events/renderPriceDetailEvents';
import TranslationManager from '../../../assets/js/common/TranslationManager';
import PriceDetailsModel from './PriceDetailsModel';

interface PriceDetailsRendererProps {
    readonly mountElement: HTMLElement;
    readonly showDeposit?: boolean;
    readonly showPayUponArrivalAsterisk?: boolean;
}

export default class PriceDetailsRenderer implements Observer {
    readonly #root: Root;

    readonly #showDeposit: boolean = true;

    readonly #showPayUponArrivalAsterisk: boolean = true;

    public constructor({
        mountElement,
        showDeposit = true,
        showPayUponArrivalAsterisk = true
    }: PriceDetailsRendererProps) {
        this.#root = createRoot(mountElement);
        this.#showDeposit = showDeposit;
        this.#showPayUponArrivalAsterisk = showPayUponArrivalAsterisk;
    }

    public async update(subject: CustomEvent): Promise<void> {
        if (!(subject instanceof CustomEvent)) {
            return;
        }

        if (subject.type === renderPriceDetailsEvents.renderPriceDetails) {
            const data = await PriceDetailsModel.toComponentProps(subject.detail);
            await this.#render({
                ...data,
                showPayUponArrivalAsterisk: this.#showPayUponArrivalAsterisk
            });
        }
    }

    async #render(props: PriceDetailsProps): Promise<void> {
        const translations = await this.#getTranslations();
        const locale = getLocale();

        const Wrapper = (): JSX.Element => (
            <TranslationsProvider locale={locale} translations={translations}>
                <PriceDetails
                    numberOfNights={props.numberOfNights}
                    rentalPrice={props.rentalPrice}
                    discountPrice={props.discountPrice}
                    includedCosts={props.includedCosts}
                    excludedCosts={props.excludedCosts}
                    afterStayCosts={props.afterStayCosts}
                    serviceFee={props.serviceFee}
                    insuranceCosts={props.insuranceCosts}
                    totalPrice={props.totalPrice}
                    depositPrice={this.#showDeposit ? props.depositPrice : undefined}
                    depositType={props.depositType}
                    showPayUponArrivalAsterisk={props.showPayUponArrivalAsterisk}
                />
            </TranslationsProvider>
        );

        this.#root.render(<Wrapper />);
    }

    async #getTranslations(): Promise<Translations> {
        const translationManager = await TranslationManager.getInstance();
        return {
            priceForNumNights: {
                label: translationManager.translate('price_for_num_nights.label')
            },
            including: {
                label: translationManager.translate('included.label')
            },
            totalPrice: {
                label: translationManager.translate('total_price.label')
            },
            serviceFee: {
                label: translationManager.translate('service_fee.label'),
                description: translationManager.translate('service_fee.description')
            },
            cancellationInsurance: {
                label: translationManager.translate('cancellation_insurance.label')
            },
            afterStayCosts: {
                title: translationManager.translate('after_stay_costs.label')
            },
            discount: {
                label: translationManager.translate('discount.title')
            },
            deposit: {
                description: translationManager.translate('safety_deposit.description')
            },
            payUponArrival: {
                description: translationManager.translate('pay_costs_directly_to_landlord.label')
            }
        };
    }
}
